import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isHoldReminderAsked, normalizeReminders } from '../../../../../../utils/missionReminders';
import {
  OPEN_WAIT_REMINDER,
  WAIT_FOR_KINDS,
  defaultWaitTitle,
  isDefaultWaitTitle,
  isWaitFulfilled,
  normalizeWaitDetails,
  normalizeWaitFor,
  waitKindLabel,
} from '../../../../../../utils/waitFor';
import { patchWaitCard } from '../../../../../../utils/waitCardEditor';
import { useMenuPosition } from '../../../StatusDropdown/hooks/useMenuPosition';
import { chooseOnPress, commitAfterPaint, hideMenuNow } from '../../../StatusDropdown/hooks/paintChoice';
import WaitKindIcon from './WaitKindIcon';

const stop = (event) => event.stopPropagation();
const DETAILS_COMMIT_DELAY = 500;
const DETAILS_MAX_LINES = 4;

/** Grow with the text, then scroll once it passes four lines. */
function fitDetailsField(field) {
  if (!field) return;
  const style = window.getComputedStyle(field);
  const line = parseFloat(style.lineHeight);
  if (!Number.isFinite(line) || line <= 0) return;
  const pad = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
  const max = line * DETAILS_MAX_LINES + pad;
  field.style.height = 'auto';
  const full = field.scrollHeight;
  field.style.height = `${Math.min(full, max)}px`;
  field.style.overflowY = full > max + 1 ? 'auto' : 'hidden';
}

function WaitKindDropdown({ task, classPrefix }) {
  const kind = normalizeWaitFor(task?.waitFor);
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const widthRef = useRef(180);
  const openedOnPress = useRef(false);
  const close = useCallback(() => setOpen(false), []);
  const { menuPosition, openMenu, showNow, commitPosition } = useMenuPosition(
    btnRef,
    menuRef,
    open,
    close,
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (btnRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onPan = () => setOpen(false);
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('flowchartpanstart', onPan);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('flowchartpanstart', onPan);
    };
  }, [open]);

  const press = (event) => {
    stop(event);
    if (event.button !== 0) return;
    if (open) return;
    const rect = btnRef.current?.getBoundingClientRect();
    widthRef.current = Math.max(rect?.width || 0, 180);
    const pos = showNow(WAIT_FOR_KINDS.length, event);
    openedOnPress.current = true;
    commitPosition(pos);
    setOpen(true);
  };

  const toggle = (event) => {
    stop(event);
    if (openedOnPress.current) {
      openedOnPress.current = false;
      return;
    }
    if (open) {
      setOpen(false);
      return;
    }
    const rect = btnRef.current?.getBoundingClientRect();
    widthRef.current = Math.max(rect?.width || 0, 180);
    openMenu(WAIT_FOR_KINDS.length, event);
    setOpen(true);
  };

  const choose = (item) => {
    hideMenuNow(menuRef.current);
    const label = btnRef.current?.querySelector('.status-dropdown__trigger-label');
    if (label) label.textContent = item.label;
    const nextTitle = defaultWaitTitle(item.id);
    if (isDefaultWaitTitle(task?.name)) {
      const name = btnRef.current?.closest('.task-node, .kanban-card, .decision-node')
        ?.querySelector('.mission-card__name-input');
      if (name) name.value = nextTitle;
    }
    commitAfterPaint(() => {
      const patch = { waitFor: item.id };
      if (isDefaultWaitTitle(task?.name)) patch.name = nextTitle;
      patchWaitCard(task.id, patch);
      setOpen(false);
    });
  };

  const menu = (
    <div
      ref={menuRef}
      className={`status-dropdown__menu mission-card__wait-menu${open ? '' : ' status-dropdown__menu--shut'}`}
      style={{
        position: 'fixed',
        top: menuPosition.top,
        left: menuPosition.left,
        width: widthRef.current,
        zIndex: 10000,
      }}
      onClick={stop}
      onMouseDown={stop}
      onPointerDown={stop}
      onWheel={stop}
    >
      {WAIT_FOR_KINDS.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`mission-card__wait-option${item.id === kind ? ' mission-card__wait-option--current' : ''}`}
          onMouseDown={(event) => chooseOnPress(event, () => choose(item))}
        >
          <WaitKindIcon kind={item.id} />
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className={`status-dropdown__row ${classPrefix}__wait-kind`}>
      <span className="status-dropdown__field-label">Wait for</span>
      <button
        ref={btnRef}
        type="button"
        className="status-dropdown__trigger status-dropdown__trigger--button"
        onPointerDown={stop}
        onMouseDown={press}
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <WaitKindIcon kind={kind} />
        <span className="status-dropdown__trigger-label">{waitKindLabel(kind)}</span>
        <span className="status-dropdown__trigger-arrow">▾</span>
      </button>
      {createPortal(menu, document.body)}
    </div>
  );
}

/**
 * Wait-for fields. Fulfilled is the only control that opens cards after this one.
 * Not yet keeps it locked and opens the reminder picker for the next check.
 */
export default function WaitForFields({ task, classPrefix = 'mission-card' }) {
  const fulfilled = isWaitFulfilled(task);
  const asking = !fulfilled && normalizeReminders(task?.reminders).some(
    (reminder) => reminder.hold && isHoldReminderAsked(reminder)
  );
  const details = task?.waitDetails || '';
  const sentence = normalizeWaitDetails(details);
  const detailsRef = useRef(null);
  const [text, setText] = useState(details);
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const lastSentRef = useRef(details);
  const taskIdRef = useRef(task.id);
  taskIdRef.current = task.id;

  // Undo or another editor. Ignore the echo of our own save.
  useEffect(() => {
    if (details === lastSentRef.current) return;
    lastSentRef.current = details;
    pendingRef.current = null;
    clearTimeout(timerRef.current);
    setText(details);
  }, [details]);

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    if (pendingRef.current === null) return;
    const next = pendingRef.current;
    pendingRef.current = null;
    lastSentRef.current = next;
    patchWaitCard(taskIdRef.current, { waitDetails: next });
  }, []);

  useEffect(() => flush, [flush]);

  useLayoutEffect(() => {
    fitDetailsField(detailsRef.current);
  }, [text]);

  const focusDetails = (event) => {
    stop(event);
    const field = detailsRef.current;
    if (!field) return;
    if (event.target !== field) field.focus();
    requestAnimationFrame(() => {
      if (detailsRef.current && document.activeElement !== detailsRef.current) {
        detailsRef.current.focus();
      }
    });
  };

  const setDetails = (event) => {
    stop(event);
    const next = event.target.value;
    setText(next);
    pendingRef.current = next;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, DETAILS_COMMIT_DELAY);
  };

  const fulfill = (event) => {
    stop(event);
    patchWaitCard(task.id, { fulfilledAt: new Date().toISOString() });
  };

  const notYet = (event) => {
    stop(event);
    window.dispatchEvent(new CustomEvent(OPEN_WAIT_REMINDER, { detail: { taskId: task.id } }));
  };

  return (
    <div className={`${classPrefix}__wait`} onMouseDown={stop} onPointerDown={stop}>
      <WaitKindDropdown task={task} classPrefix={classPrefix} />

      <label
        className={`${classPrefix}__wait-field ${classPrefix}__wait-field--details`}
        onMouseDown={focusDetails}
      >
        <span className="status-dropdown__field-label">Details</span>
        <textarea
          ref={detailsRef}
          className={`${classPrefix}__wait-details`}
          value={text}
          rows={1}
          placeholder="What has to arrive before you can continue?"
          onChange={setDetails}
          onBlur={flush}
          onMouseDown={focusDetails}
          onPointerDown={focusDetails}
          onClick={stop}
          onKeyDown={stop}
        />
      </label>

      {fulfilled && (
        <div className={`${classPrefix}__wait-fulfilled`}>Fulfilled</div>
      )}

      {!fulfilled && (
        <div className={`${classPrefix}__wait-ask`}>
          {asking && (
            <p className={`${classPrefix}__wait-ask-copy`}>
              {sentence || 'Is this fulfilled?'}
            </p>
          )}
          <div className={`${classPrefix}__wait-ask-actions`}>
            {asking && (
              <button type="button" className={`${classPrefix}__wait-btn`} onClick={notYet} onMouseDown={stop}>
                Not yet
              </button>
            )}
            <button
              type="button"
              className={`${classPrefix}__wait-btn ${classPrefix}__wait-btn--fulfill`}
              onClick={fulfill}
              onMouseDown={stop}
            >
              Fulfilled
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
